import Graphic from '@arcgis/core/Graphic';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils.js';

// Controllers
import mapController from '../controllers/MapControllers';

// Store
import store from '../store/store';
import { setMapLayersUpdating } from '../store/slices/mapSlice';

// Utils
import { generateAdmin1UnionGeomatriesFromAdmin2Features } from './admin1Map.utils';

// Configs
import {
  ADMIN_1_BOUNDARY_SYMBOL,
  ADMIN_2_BOUNDARY_SYMBOL,
  ADMIN_2_INDICATOR,
  ADMIN_2_SURVEY_TYPE,
  ADMIN_2_YEAR,
  NO_VALUE,
  RASTER_URL_CONFIG,
} from '../configs/map.configs';
import { SurveySelectedType } from '../configs/types';

/**
 * Get feature layer ID
 * @returns {string} - Feature layer ID
 */
export function getFeatureLayerID(adminPreferred?: string): string {
  const { adminSelected } = store.getState().sidebarMapFiltersSlice;
  const { surveySelected } = store.getState().admin2EstimatesSlice;
  const countryCode = surveySelected?.DHSCC;
  const SurveyType = surveySelected?.SurveyType;
  const year = surveySelected?.year;

  const featureLayerID = `${adminPreferred || adminSelected}__${countryCode}_${year}_${SurveyType}`;
  return featureLayerID;
}

/**
 * Get the selected features from the DHS Regions layer and indicator value from DHS API response
 * @param {Object} feature - Admin 1 feature
 * @param {Object} indicatorsObject - Indicators object from DHS API response
 * @returns {number} - Indicator value
 */
export function getAdmin1Value(feature: any, indicatorsObject: any): number {
  const regionID = feature?.attributes?.REG_ID;
  const indicatorListElement = indicatorsObject[regionID];
  const Value = indicatorListElement?.Value;

  return Value || NO_VALUE;
}

/**
 * Get admin 1 Graphics
 * @param {Object} admin1FeaturesWithGeometries - Admin 1 features layer query response
 * @param {Object} indicatorsObject - Indicators object from DHS API response
 * @returns {Array} - Admin 1 graphics
 */
export function getAdmin1Graphics(admin1FeaturesWithGeometries: any, indicatorsObject: any): Array<any> {
  const admin1Graphics = admin1FeaturesWithGeometries?.features.map((feature: any) => {
    const Value = getAdmin1Value(feature, indicatorsObject);

    const regionGraphic = new Graphic({
      geometry: feature?.geometry,
      attributes: {
        ...feature?.attributes,
        Value: Value,
      },
    });

    return regionGraphic;
  });

  return admin1Graphics;
}

export function getAdmin2AddedAttributes(admin2FeaturesDataAttributes: any) {
  const Value = admin2FeaturesDataAttributes?.Value || NO_VALUE;
  const Population_at_risk = admin2FeaturesDataAttributes?.Population_at_risk || NO_VALUE;
  const Uncertainty_Interval = admin2FeaturesDataAttributes?.Uncertainty_Interval || NO_VALUE;
  const regionId = admin2FeaturesDataAttributes?.region_id;

  const admin2AddedAttributes = {
    Value: Value,
    Population_at_risk: Population_at_risk,
    Uncertainty_Interval: Uncertainty_Interval,
    region_id: regionId,
  };

  return admin2AddedAttributes;
}

/**
 * Get the key for combining admin2 and admin1 names.
 * @param {string} admin2name - Admin 2 name.
 * @param {string} admin1name - Admin 1 name.
 * @returns {string} - Key combining admin2 and admin1 names.
 */
function getAdmin2nameAdmin1nameKey(admin2name: string, admin1name: string): string {
  return `${admin2name}__${admin1name}`;
}

export function getAdmin2FeaturesDataFiltered(
  admin2FeaturesData: any,
  indicatorID: string,
  surveySelected: SurveySelectedType | null
) {
  const surveyYear = surveySelected?.year;
  const surveyType = surveySelected?.SurveyType;
  const admin2FeaturesDataFiltered: any = {};
  const admin2Counts: any = {};

  admin2FeaturesData
    .filter((feature: any) => {
      const featureIndicatorID = feature?.attributes[ADMIN_2_INDICATOR];
      const featureSurveyType = feature?.attributes[ADMIN_2_SURVEY_TYPE];
      const featureYear = feature?.attributes[ADMIN_2_YEAR];

      const matchingFeature =
        featureIndicatorID === indicatorID && featureSurveyType === surveyType && featureYear === surveyYear;

      if (matchingFeature) {
        admin2Counts[feature.attributes.adm2_name] = admin2Counts[feature.attributes.adm2_name] + 1 || 1;
      }

      return matchingFeature;
    })
    .forEach((feature: any) => {
      const admin2name = feature.attributes.adm2_name;

      if (admin2Counts[admin2name] === 1) {
        admin2FeaturesDataFiltered[admin2name] = feature.attributes;
      } else {
        const admin1name = feature.attributes.adm1_name;
        const admin2name_admin1name = getAdmin2nameAdmin1nameKey(admin2name, admin1name);

        admin2FeaturesDataFiltered[admin2name_admin1name] = feature.attributes;
      }
    });

  return admin2FeaturesDataFiltered;
}

/**
 * Get the attributes of an admin 2 feature based on the provided admin2AttributesObject and feature.
 * @param {Object} admin2AttributesObject - Object containing admin 2 features attributes.
 * @param {Object} feature - Feature object containing admin 1 and admin 2 names.
 * @returns {Object} - Object containing admin 2 name, admin 1 name, and admin 2 features attributes.
 */
export function getAdmin2FeaturesAttributes(
  admin2AttributesObject: any,
  feature: any
): { admin2name: string; admin1name: string; admin2FeaturesAttributes: any } {
  const admin2name = feature.attributes.ADM2_NAME;
  const admin1name = feature.attributes.ADM1_NAME;
  const admin2name_admin1name = getAdmin2nameAdmin1nameKey(admin2name, admin1name);

  const admin2FeaturesAttributes = admin2AttributesObject[admin2name_admin1name] || admin2AttributesObject[admin2name];

  if (!admin2FeaturesAttributes) {
    admin2Debugging(admin2name, admin1name, admin2AttributesObject);
  }

  return { admin2name, admin1name, admin2FeaturesAttributes };
}

/**
 * Debugs admin 2 features attributes to find possible matching features
 * @param {string} admin2name - Admin 2 name.
 * @param {string} admin1name - Admin 1 name.
 * @param {any} admin2AttributesObject - Object containing all admin 2 features attributes.
 * @returns {void} - No return value. Logs debugging information to the console.
 */
export function admin2Debugging(admin2name: string, admin1name: string, admin2AttributesObject: any): void {
  console.warn(`=== Not matching. (Geometries Admin 1 & Admin 2: ${admin1name} - ${admin2name}) ===`);
  const possibleMatchingFeatures = Object.keys(admin2AttributesObject)
    .filter((key) => {
      return key.startsWith(admin2name);
    })
    .map((key) => {
      return admin2AttributesObject[key];
    });
  console.warn(`=== Possible Matching Features  ===`);
  console.table(possibleMatchingFeatures, ['adm0_name', 'adm1_name', 'adm2_name']);
}

/**
 * Get admin 2 Graphics
 * @param {Object} admin2FeaturesData - Admin 2 features layer query response with attributes
 * @param {Object} admin2FeaturesWithGeometries - Admin 2 features layer query response
 * @param {string} indicatorID - Indicator ID (e.g. 'CN_NUTS_C_HA2')
 * @param {SurveySelectedType} surveySelected - Selected survey
 * @returns {Array} - Admin 2 graphics
 */
export function getAdmin2Graphics(
  admin2FeaturesData: any,
  admin2FeaturesWithGeometries: any,
  indicatorID: string,
  surveySelected: SurveySelectedType | null
): Array<any> {
  const admin2AttributesObject = getAdmin2FeaturesDataFiltered(admin2FeaturesData, indicatorID, surveySelected);
  const admin2Graphics = admin2FeaturesWithGeometries?.features.map((feature: any) => {
    const { admin2FeaturesAttributes } = getAdmin2FeaturesAttributes(admin2AttributesObject, feature);

    const admin2AddedAttributes = getAdmin2AddedAttributes(admin2FeaturesAttributes);
    const attributes = {
      ...feature?.attributes,
      ...admin2AddedAttributes,
    };

    const regionGraphic = new Graphic({
      geometry: feature?.geometry,
      attributes: attributes,
    });

    return regionGraphic;
  });

  return admin2Graphics;
}

/**
 * Get the admin 1 boundaries graphics.
 * @param {any[]} adminGraphics - Admin 1 graphics data.
 * @returns {__esri.Graphic[]} - Admin 1 boundaries graphics.
 */
export function getAdmin1BoundariesGraphics(adminGraphics: any[]): __esri.Graphic[] {
  const graphics = adminGraphics.map((graphic: any) => {
    const graphicObject = new Graphic({
      geometry: graphic.geometry,
      symbol: ADMIN_1_BOUNDARY_SYMBOL as __esri.SimpleLineSymbolProperties,
    });

    return graphicObject;
  });

  return graphics;
}

export async function getAdmin2BoundariesGraphics(surveySelected: SurveySelectedType) {
  const countryCode = surveySelected?.ADM0;
  const geometries = await generateAdmin1UnionGeomatriesFromAdmin2Features(countryCode);

  const boundariesGraphics = Object.values(geometries).map((geometry: any) => {
    const graphic = new Graphic({
      geometry,
      symbol: ADMIN_2_BOUNDARY_SYMBOL as __esri.SimpleLineSymbolProperties,
    });
    return graphic;
  });

  return boundariesGraphics;
}

/**
 * Handle Admin (1 || 2) boundaries graphics
 * @param {__esri.GraphicsLayer} adminBoundariesGraphicsLayer - Admin (1 || 2) boundaries graphics layer
 * @param {any[]} boundariesGraphics - Admin graphics
 * @param {SurveySelectedType} surveySelected - Survey selected
 * @param {string} adminBoundariesLayerID - Admin boundaries layer ID
 */
export const handleAdminBoundariesGraphics = async (
  adminBoundariesGraphicsLayer: __esri.GraphicsLayer,
  boundariesGraphics: any[],
  surveySelected: SurveySelectedType,
  adminBoundariesLayerID: string
) => {
  const countryName = surveySelected?.ADM0;
  const adminBoundariesGraphicsLayerIdNew = `${adminBoundariesLayerID}__${countryName}`;

  if (adminBoundariesGraphicsLayer.id !== adminBoundariesGraphicsLayerIdNew) {
    adminBoundariesGraphicsLayer.removeAll();
    adminBoundariesGraphicsLayer.id = adminBoundariesGraphicsLayerIdNew;
    adminBoundariesGraphicsLayer.addMany(boundariesGraphics);
  }

  reorderAdminBoundariesLayerToTop(adminBoundariesGraphicsLayer);
};

/**
 * Reorders the Admin 1 or Admin 2 boundaries graphics layer to the top of the Map layers. Defaults to Admin 1 boundaries graphics layer.
 * @param {__esri.GraphicsLayer | undefined} [adminBoundariesGraphicsLayer=mapController.admin1BoundariesGraphicsLayer] - Admin 1 or Admin 2 boundaries graphics layer to be reordered. Defaults to admin1BoundariesGraphicsLayer.
 */
export const reorderAdminBoundariesLayerToTop = (
  adminBoundariesGraphicsLayer: __esri.GraphicsLayer | undefined = mapController.admin1BoundariesGraphicsLayer
) => {
  if (adminBoundariesGraphicsLayer) {
    mapController.map?.reorder(adminBoundariesGraphicsLayer, mapController.map?.layers.length - 1);
  }
};

/**
 * Reorder the Admin 2 Reference FeatureLayer to the top of the Map layers
 */
export const reorderAdmin2ReferenceFeatureLayerToTop = () => {
  if (mapController.admin2ReferenceFeatureLayer) {
    mapController.map?.reorder(mapController.admin2ReferenceFeatureLayer, mapController.map?.layers.length - 1);
  }
};

/**
 * Reorder the Admin 2 Reference FeatureLayer to the top of the Map layers
 */
export const reorderPopupMarkerGraphicsLayerToTop = () => {
  if (mapController.popupMarkerGraphicsLayer) {
    mapController.map?.reorder(mapController.popupMarkerGraphicsLayer, mapController.map?.layers.length - 1);
  }
};

/**
 * Get the selected survey ID
 * @param {SurveySelectedType} surveySelected - Selected survey
 * @param {string} indicatorID - Indicator ID
 * @returns {string} - Country survey ID
 */
function getSelectedSurveyID(surveySelected: SurveySelectedType, indicatorID: string): string {
  const year = surveySelected.year;
  const surveyType = surveySelected.SurveyType;
  const countryCode = surveySelected.DHSCC;

  return `${year}_${surveyType}_${indicatorID}_${countryCode}`;
}

/**
 * Get the selected sublayer ID
 * @param {SurveySelectedType} surveySelected - Selected survey
 * @param {string} indicatorID - Indicator ID (e.g. 'CN_NUTS_C_HA2')
 * @param {string} fieldSelected - Filter option selected field
 */
export const getSelectedSubLayerID = (
  surveySelected: SurveySelectedType,
  indicatorID: string,
  fieldSelected: string
) => {
  const countrySurveyID = getSelectedSurveyID(surveySelected, indicatorID);

  let subLayerID: string;
  const filter = RASTER_URL_CONFIG[fieldSelected];
  if (filter !== '') {
    subLayerID = `${countrySurveyID}${`_${filter}`}`;
  } else {
    subLayerID = `${countrySurveyID}${''}`;
  }

  return subLayerID;
};

// Watches layer updating and sets map layers updating state
export const setLayerUpdatingWatcher = (mapView: any, layer: any) => {
  mapView.whenLayerView(layer).then((layerView: __esri.LayerView) => {
    reactiveUtils.watch(
      () => layerView.updating,
      () => {
        store.dispatch(setMapLayersUpdating(mapView.layerViewManager.updating));
      }
    );
  });

  // Watch for layer updating on admin 1 client side features layer and set map layers boundary visibility for admin 1 and admin 2
  reactiveUtils.watch(
    () => mapController.admin1ClientSideFeaturesLayer?.visible,
    (admin1ClientSideFeaturesLayerVisible) => {
      if (mapController.admin1BoundariesGraphicsLayer && mapController.admin2BoundariesGraphicsLayer) {
        if (admin1ClientSideFeaturesLayerVisible) {
          mapController.admin1BoundariesGraphicsLayer.visible = true;
          mapController.admin2BoundariesGraphicsLayer.visible = false;
        } else {
          mapController.admin1BoundariesGraphicsLayer.visible = false;
          mapController.admin2BoundariesGraphicsLayer.visible = true;
        }
      }
    }
  );
};
